import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { toInstant } from '@/features/anomalies/api'

/**
 * Shift handovers. LIVE.
 *
 * `/v1/shifts/handovers` — list, detail, create, update, delete and two SSE
 * streams, every route behind `shift_handovers.manage` OR
 * `shift_handovers.prepare`. The schemas are `app/schemas/shifts/handover.py`.
 *
 * ── Not the demo archive ──────────────────────────────────────────────────
 * `features/handovers` is the prototype's handover: drafted, published and
 * acknowledged by the incoming lead, with a composed report. The API has none of
 * that. A handover here is a note plus three lists of things worth flagging to
 * the next shift — work orders, log book entries and anomalies — filed against a
 * shift, a date and one place. The two modules are kept apart rather than one
 * rewritten into the other, because Home and the demo Shifts screen still read
 * the demo one.
 *
 * ── What the server refuses, so the form can refuse it first ──────────────
 *   - Every key on every write. `plant_id`/`line_id`/`machine_id` are nullable
 *     with no default, and omitting a nullable key is a 400, not a null.
 *   - Exactly one of the three location ids (`ExactlyOneParentMixin`).
 *   - `shift_id` must be a shift the CREATOR is rostered on for `shift_date`
 *     (`verify_actor_on_shift`; root is exempt) — 400 keyed `shift_id`.
 *   - One handover per creator per shift, date and place — 400 keyed `shift_id`.
 *   - A linked item must sit at EXACTLY the handover's place: a line-level
 *     handover cannot link a work order raised on one of that line's machines.
 *   - Linking needs the other module's grant: `work_orders.manage` or
 *     `.update_assigned`, `logbook.manage` or `.record_entries`,
 *     `anomalies.manage`. Only NEW links are checked on an update.
 *
 * ── Create-only and the rest ──────────────────────────────────────────────
 * The place, the shift and the date are fixed at creation, like a log book
 * entry's. An update is a full replace of the notes and all three lists.
 */

/** `Field(min_length=1, max_length=2000)` on `notes`. */
export const NOTES_MAX_LENGTH = 2000

/** `request_list_max_items` — the cap on each id list a body carries. */
export const LINKS_MAX = 500

/** The largest page the server serves — `page_size_max`. */
export const MAX_PAGE_SIZE = 200

export const DEFAULT_PAGE_SIZE = 25

/** `sort_by`'s whole `Literal`. Anything else is a 400. */
export const SORT_FIELDS = ['created_at', 'shift_date']

/**
 * The location trio → which tier it is and the id.
 *
 * Exactly one is set on every row, so this is the single place that decides
 * which. The app calls a plant a `site` and a machine an `asset` — see
 * `features/hierarchy/api.js` on the two vocabularies.
 */
export function locationOf(row) {
  if (row.machineId) return { tier: 'asset', id: row.machineId }
  if (row.lineId) return { tier: 'line', id: row.lineId }
  if (row.plantId) return { tier: 'site', id: row.plantId }
  return null
}

/** Whether two rows hang off exactly the same place — the server's `_same_location`. */
export const sameLocation = (a, b) =>
  (a.plantId ?? null) === (b.plantId ?? null) &&
  (a.lineId ?? null) === (b.lineId ?? null) &&
  (a.machineId ?? null) === (b.machineId ?? null)

/**
 * Wire → app.
 *
 * The list item and the detail share every field but these: the list sends the
 * three COUNTS, the detail sends `notes`, `shift_detail`, the three summary
 * arrays and `updated_at`. Both go through here so a row from either is the same
 * object with the absent half left null.
 *
 * The counts and the arrays can disagree for one handover: a count is of link
 * rows, while the detail drops a linked item that was since deleted or sits
 * under a deactivated place. The detail is the one to believe. Recorded in
 * BACKEND-GAPS.md.
 */
export const toHandover = (row) => ({
  id: row.id,
  /* `HO-0001` — what a person reads and quotes. */
  serialNumber: row.serial_number ?? row.id,

  shiftId: row.shift_id ?? null,
  /* A calendar date, `YYYY-MM-DD`. Never formatted against a timezone: it is a
     day, not an instant, and moving it would move a night shift across midnight. */
  shiftDate: row.shift_date ?? null,
  /* Detail only. Null when the shift has since been deleted. */
  shift: row.shift_detail
    ? {
        name: row.shift_detail.name ?? '',
        type: row.shift_detail.type ?? null,
        active: row.shift_detail.is_active === true,
      }
    : null,

  plantId: row.plant_id ?? null,
  lineId: row.line_id ?? null,
  machineId: row.machine_id ?? null,
  /* `{plant, line, machine}` — names only, no ids. */
  hierarchy: row.hierarchy ?? {},

  createdBy: row.created_by ?? null,
  /* Naive UTC on the wire; `toInstant` restores the `Z` so it is not read as
     local time. See `features/anomalies/api.js`. */
  createdAt: toInstant(row.created_at),
  updatedAt: toInstant(row.updated_at),

  /* Detail only — null on a list row, where "not fetched" is not "empty". */
  notes: row.notes ?? null,
  workOrders: Array.isArray(row.work_orders)
    ? row.work_orders.map((order) => ({
        id: order.id,
        serialNumber: order.serial_number,
        title: order.title ?? '',
        status: order.status,
        priority: order.priority,
      }))
    : null,
  logbookEntries: Array.isArray(row.logbook_entries)
    ? row.logbook_entries.map((entry) => ({
        id: entry.id,
        serialNumber: entry.serial_number,
        title: entry.title ?? '',
        category: entry.category,
        severity: entry.severity,
      }))
    : null,
  anomalies: Array.isArray(row.anomalies)
    ? row.anomalies.map((anomaly) => ({
        id: anomaly.id,
        serialNumber: anomaly.serial_number,
        title: anomaly.title ?? '',
        severity: anomaly.severity,
        status: anomaly.status,
      }))
    : null,

  /* List only. Read from the arrays on a detail row so a card can show a count
     from either shape without asking which endpoint it came from. */
  workOrderCount: row.work_order_count ?? row.work_orders?.length ?? 0,
  logbookEntryCount: row.logbook_entry_count ?? row.logbook_entries?.length ?? 0,
  anomalyCount: row.anomaly_count ?? row.anomalies?.length ?? 0,
})

/**
 * One page of handovers.
 *
 * Params are snake_case on the wire: this API now 400s on an unknown param, and
 * before it did, a camelCase key was silently ignored and answered the
 * unfiltered page 1.
 *
 * @param {{
 *   search?: string, shiftId?: string, from?: string, to?: string,
 *   plantId?: string, lineId?: string, machineId?: string,
 *   sortBy?: 'created_at'|'shift_date', sortDir?: 'asc'|'desc',
 *   page?: number, pageSize?: number,
 * }} params
 */
export async function fetchShiftHandovers(params = {}, signal) {
  const {
    search,
    shiftId,
    from,
    to,
    plantId,
    lineId,
    machineId,
    sortBy = 'created_at',
    sortDir = 'desc',
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = params

  const data = await get(endpoints.shiftHandovers.list, {
    signal,
    params: {
      ...(search?.trim() ? { search: search.trim() } : {}),
      ...(shiftId ? { shift_id: shiftId } : {}),
      ...(from ? { shift_date_from: from } : {}),
      ...(to ? { shift_date_to: to } : {}),
      ...(plantId ? { plant_id: plantId } : {}),
      ...(lineId ? { line_id: lineId } : {}),
      ...(machineId ? { machine_id: machineId } : {}),
      sort_by: SORT_FIELDS.includes(sortBy) ? sortBy : 'created_at',
      sort_dir: sortDir === 'asc' ? 'asc' : 'desc',
      page,
      page_size: pageSize,
    },
  })

  return {
    items: (data?.shift_handovers ?? []).map(toHandover),
    /* Echoed back rather than assumed: a clamped page must move the footer. */
    page: data?.page ?? page,
    pageSize: data?.page_size ?? pageSize,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 0,
  }
}

export async function fetchShiftHandover(id, signal) {
  return toHandover(await get(endpoints.shiftHandovers.detail(id), { signal }))
}

/** The three id lists, as the wire spells them. Deduplicated server-side too. */
const toLinks = ({ workOrderIds, logbookEntryIds, anomalyIds }) => ({
  work_order_ids: [...new Set(workOrderIds ?? [])],
  logbook_entry_ids: [...new Set(logbookEntryIds ?? [])],
  anomaly_ids: [...new Set(anomalyIds ?? [])],
})

/**
 * File a handover.
 *
 * All three location keys travel, exactly one non-null. The creator is NOT
 * sent — the server stamps `created_by` from the token.
 */
export async function createShiftHandover({
  plantId,
  lineId,
  machineId,
  shiftId,
  shiftDate,
  notes,
  ...links
}) {
  return toHandover(
    await post(endpoints.shiftHandovers.list, {
      plant_id: plantId ?? null,
      line_id: lineId ?? null,
      machine_id: machineId ?? null,
      shift_id: shiftId,
      shift_date: shiftDate,
      notes: notes.trim(),
      ...toLinks(links),
    }),
  )
}

/**
 * Update one — the notes and the three lists, and nothing else.
 *
 * `ShiftHandoverUpdate` declares exactly these four, and an undeclared key is a
 * 400, so the place, shift and date must NOT be spread in here.
 */
export async function updateShiftHandover({ id, notes, ...links }) {
  return toHandover(
    await put(endpoints.shiftHandovers.detail(id), { notes: notes.trim(), ...toLinks(links) }),
  )
}

/** Soft on the server. No undo on any screen. */
export async function deleteShiftHandover(id) {
  await del(endpoints.shiftHandovers.detail(id))
  return { id, deleted: true }
}
